/**
 * 2026-05-25 — local-only follow-market split button used in the
 * experimental crypto-price-style market view. Main action button
 * applies the user's selected follow-targets (topic feed / bookmark /
 * notifications). Adjacent chevron opens a popover with per-target
 * checkboxes so the user can opt in/out of each channel before
 * triggering the action. Newsletter target is locked "Coming soon".
 *
 * Bookmark folds into this control — the standalone Bookmark icon
 * chip in ExperimentalMarketActions can be removed once this ships.
 *
 * UI-only for now; wire each target to its real backend endpoint
 * (POST /v1/markets/:slug/follow, POST /v1/me/bookmarks, etc.) once
 * the UX is locked.
 */
"use client";

import { useEffect, useRef, useState } from "react";
import {
  Check,
  ChevronDown,
  Plus,
  Hash,
  Bookmark,
  Bell,
  Mail,
  type LucideIcon,
} from "lucide-react";

interface Props {
  marketId: string;
}

interface Selection {
  topic: boolean;
  bookmark: boolean;
  notifications: boolean;
  newsletter: boolean;
}

const DEFAULTS: Selection = {
  topic: true,
  bookmark: true,
  notifications: true,
  newsletter: false,
};

export function MarketFollowButton({ marketId: _marketId }: Props) {
  const [following, setFollowing] = useState(false);
  const [selection, setSelection] = useState<Selection>(DEFAULTS);
  const [open, setOpen] = useState(false);
  const [unfollowHover, setUnfollowHover] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  function toggleFollow() {
    setFollowing((v) => !v);
  }

  function toggleTarget(key: keyof Selection) {
    if (key === "newsletter") return;
    setSelection((s) => ({ ...s, [key]: !s[key] }));
  }

  const activeCount =
    (selection.topic ? 1 : 0) +
    (selection.bookmark ? 1 : 0) +
    (selection.notifications ? 1 : 0);

  // Color tokens
  const SURFACE = "#F2F3F5";
  const SURFACE_HOVER = "#E7E9EC";
  const FOLLOWING_BG = "#E7E9EC";
  const TEXT = "#0A0B0D";
  const TEXT_SECONDARY = "#5B616E";
  const DANGER_BG = "rgba(207, 32, 47, 0.06)";
  const DANGER_TEXT = "#CF202F";

  const mainBg = following
    ? unfollowHover
      ? DANGER_BG
      : FOLLOWING_BG
    : SURFACE;
  const mainColor = following && unfollowHover ? DANGER_TEXT : TEXT;
  const mainLabel = following
    ? unfollowHover
      ? "Unfollow"
      : "Following"
    : "Follow";

  return (
    <div ref={wrapRef} className="relative inline-flex">
      <button
        type="button"
        onClick={toggleFollow}
        onMouseEnter={() => following && setUnfollowHover(true)}
        onMouseLeave={() => setUnfollowHover(false)}
        className="inline-flex items-center gap-1.5 rounded-l-full px-3 py-1.5 text-[12px] font-semibold transition-colors"
        style={{ background: mainBg, color: mainColor }}
        aria-pressed={following}
        aria-label={
          following ? "Unfollow market" : "Follow market — applies selected targets"
        }
      >
        {following && !unfollowHover ? (
          <Check size={12} strokeWidth={2.25} aria-hidden="true" />
        ) : !following ? (
          <Plus size={12} strokeWidth={2.25} aria-hidden="true" />
        ) : null}
        {mainLabel}
      </button>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center rounded-r-full pl-1 pr-2 py-1.5 transition-colors"
        style={{
          background: mainBg,
          color: mainColor,
          borderLeft: `1px solid rgba(15, 23, 42, ${following ? 0.05 : 0.06})`,
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Follow options"
        onMouseEnter={(e) => {
          if (!following) e.currentTarget.style.background = SURFACE_HOVER;
        }}
        onMouseLeave={(e) => {
          if (!following) e.currentTarget.style.background = SURFACE;
        }}
      >
        <ChevronDown
          size={13}
          strokeWidth={2}
          aria-hidden="true"
          className="transition-transform"
          style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
        />
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Follow targets"
          className="absolute right-0 top-[calc(100%+6px)] z-40 w-[280px] overflow-hidden rounded-2xl"
          style={{
            background: "#FFFFFF",
            boxShadow:
              "0 1px 3px rgba(15, 23, 42, 0.06), 0 12px 32px rgba(15, 23, 42, 0.10)",
          }}
        >
          <div className="px-4 pt-3.5 pb-2">
            <div className="text-[10px] font-semibold uppercase tracking-[0.08em]" style={{ color: TEXT_SECONDARY }}>
              When following
            </div>
            <div className="mt-0.5 text-[11px]" style={{ color: TEXT_SECONDARY }}>
              {activeCount} of 3 channels selected
            </div>
          </div>
          <ul className="pb-1.5">
            <Option
              icon={Hash}
              label="Follow topic page"
              description="Updates show in your feed"
              checked={selection.topic}
              onToggle={() => toggleTarget("topic")}
            />
            <Option
              icon={Bookmark}
              label="Bookmark"
              description="Save to your list"
              checked={selection.bookmark}
              onToggle={() => toggleTarget("bookmark")}
            />
            <Option
              icon={Bell}
              label="Notifications"
              description="Resolution + 5%+ price moves"
              checked={selection.notifications}
              onToggle={() => toggleTarget("notifications")}
            />
            <Option
              icon={Mail}
              label="Newsletter digest"
              description="Coming soon"
              checked={false}
              disabled
            />
          </ul>
        </div>
      )}
    </div>
  );
}

function Option({
  icon: Icon,
  label,
  description,
  checked,
  onToggle,
  disabled,
}: {
  icon: LucideIcon;
  label: string;
  description: string;
  checked: boolean;
  onToggle?: () => void;
  disabled?: boolean;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onToggle}
        disabled={disabled}
        role="menuitemcheckbox"
        aria-checked={checked}
        className="flex w-full items-start gap-3 px-4 py-2.5 text-left transition-colors enabled:hover:bg-[#F7F8FA] disabled:cursor-not-allowed disabled:opacity-50"
      >
        <span
          className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full"
          style={{ background: "#F2F3F5" }}
        >
          <Icon size={13} strokeWidth={1.75} style={{ color: "#5B616E" }} aria-hidden="true" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[13px] font-semibold" style={{ color: "#0A0B0D" }}>
            {label}
          </span>
          <span className="block text-[11px]" style={{ color: "#5B616E" }}>
            {description}
          </span>
        </span>
        <span
          className="mt-0.5 flex h-[18px] w-[18px] flex-shrink-0 items-center justify-center rounded-md transition-colors"
          style={{
            background: checked && !disabled ? "#0A0B0D" : "transparent",
            border: `1.5px solid ${
              checked && !disabled ? "#0A0B0D" : "#D1D5DB"
            }`,
          }}
          aria-hidden="true"
        >
          {checked && !disabled && (
            <Check size={11} strokeWidth={3} style={{ color: "#FFFFFF" }} />
          )}
        </span>
      </button>
    </li>
  );
}
